import MapboxDraw, { DrawCustomMode, DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { Feature, Polygon } from "geojson";
import { defer, uniqBy } from "lodash";
import { LngLatLike } from "mapbox-gl";
import { passing_draw_polygon as passingDrawPolygon } from "mapbox-gl-draw-passing-mode";

import { bbox, booleanContains, booleanIntersects, booleanPointInPolygon } from "fond/turf";
import { isLineString, isPoint } from "fond/types/geojson";

import { evaluateFilterExpression } from "./helper";

/**
 * Custom draw mode for selecting features by drawing a polygon. This mode extends the passing_draw_polygon mode from mapbox-gl-draw-passing-mode.
 */
const polygonSelect: DrawCustomMode = {
  ...passingDrawPolygon,

  onSetup: function (opts) {
    const state = passingDrawPolygon?.onSetup?.call(this, opts);

    // if no onDraw was set we default to our internal onDraw function that handles selection of features within the drawn polygon
    state.onDraw = opts.onDraw || this.onDraw.bind(this);
    return state;
  },

  onDraw: function (this: MapboxDraw.DrawCustomModeThis, polygon: Feature<Polygon>) {
    if (this.map) {
      // Get the bounding box of the polygon
      // and convert it to a bounding box in pixel coordinates
      // to use with queryRenderedFeatures
      const polygonBounds = bbox(polygon);
      const southWest = [polygonBounds[0], polygonBounds[1]] as LngLatLike;
      const northEast = [polygonBounds[2], polygonBounds[3]] as LngLatLike;
      const northEastPointPixel = this.map.project(northEast);
      const southWestPointPixel = this.map.project(southWest);

      // Get all features within the bounding box
      // and filter them to only include those that are within the polygon
      const features = uniqBy(
        this.map
          ?.queryRenderedFeatures([southWestPointPixel, northEastPointPixel], {
            layers: [
              // Select all features within the drawControl styles
              ...(this._ctx.options.styles?.map(({ id }) => id) || []),
              // Select all features within the layers specified in the selectableLayerIds
              ...(this._ctx.options.selectableLayerIds || []),
            ],
          })
          .map((f) => {
            // We need to get the feature from the drawControl, we cannot rely on the geometry within the feature
            // returned from the click function above.
            const drawnFeature = this.getFeature(f.properties?.id)?.toGeoJSON() || f;
            return drawnFeature;
          })
          .filter((f) => {
            if (f.id || f.properties?.id !== undefined) {
              // If editingLayerId is defined, we only select features from that layer
              if (this._ctx.options.editOptions?.editingLayerId && "properties" in f) {
                // For sublayers, use the parent layer ID; otherwise use the editing layer ID
                const layerIdToMatch = this._ctx.options.parentLayerId || this._ctx.options.editOptions.editingLayerId;
                if (layerIdToMatch !== f.properties?.layerId) {
                  return false;
                }

                // If we're editing a sublayer with a filter, apply the filter expression
                if (this._ctx.options.sublayerFilter && this._ctx.options.sublayerFilter.length > 0) {
                  if (!evaluateFilterExpression(this._ctx.options.sublayerFilter, f)) {
                    return false;
                  }
                }
              }
              // check if feature is within or intersects the polygon
              if (isPoint(f)) {
                return booleanPointInPolygon(f, polygon);
              } else if (isLineString(f)) {
                return booleanContains(polygon, f) || booleanIntersects(f, polygon);
              }
            }
            return false;
          }),
        (f) => f.properties?.id || f.id
      );

      defer(() => {
        this.changeMode("simple_select", { featureIds: features.map((f) => f.properties?.id || f.id) });
        this.map?.fire("draw.modechange", { type: "draw.modechange", mode: "simple_select" } as DrawModeChangeEvent);
        this.map?.fire("draw.selectionchange", { features });
      });
    }
  },
};

export default polygonSelect;
